/**
 * What the response chart shows: how wide a window (`span`), where it starts
 * (`from`), whether it follows the newest check (`pinned`), and the native
 * horizontal scroll that moves it.
 *
 * The scroll is virtual. The frame is an `overflow-x: auto` box around a
 * spacer as wide as the whole history would be at this zoom
 * (`scrollGeometry`), and the plot inside it is sticky at the left edge, so
 * only the visible window is ever drawn while the browser supplies what a
 * hand-made scroller never gets right: momentum, the trackpad's two-finger
 * swipe, shift + wheel, a touch fling.
 *
 * Two sources of truth have to be kept from fighting:
 *
 *  - A scroll by the reader moves `from` (one state update per animation
 *    frame at most) and never writes scrollLeft back. Writing it back from a
 *    frame-old state would drag a momentum scroll backwards.
 *  - A move made in code (zoom, a key, the navigator, "to now") sets `from`
 *    and marks the state `by: "code"`; a layout effect then puts scrollLeft
 *    where that `from` is, after the spacer has its new width and before the
 *    frame is painted. The scroll event that write causes is recognised and
 *    swallowed, so it does not round `from` to the pixel.
 *
 * Pinned is the reader's intent, not a position. Scrolled to the right end
 * (within 2 px), the view stays glued to now as new checks move the history's
 * end; scrolled away, it stays on the time it shows.
 *
 * All the math (clamps, the zoom ladder, zoom around a point, pixels to time)
 * is `lib/monitoringViewport.ts`; this file only wires it to the DOM.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import {
  DEFAULT_SPAN_MS,
  clampFrom,
  clampSpan,
  fromOfScroll,
  nextZoomSpan,
  scrollGeometry,
  scrollOfFrom,
  zoomAround,
  type ScrollGeometry,
  type TimeRange,
  type TimeView,
} from "../../../../lib/monitoring";
import { PLOT_LEFT, plotWidthOf } from "./layout";

/** Scrolled this close to the right end counts as following now. */
const PIN_PX = 2;
/** An arrow key moves the window by this share of its width. */
const PAN_SHARE = 0.25;

interface ViewState {
  from: number;
  span: number;
  pinned: boolean;
  /** Who moved it last: a reader's scroll is never written back to scrollLeft. */
  by: "scroll" | "code";
}

interface Live {
  range: TimeRange;
  geo: ScrollGeometry;
  plotWidth: number;
  view: TimeView & { pinned: boolean };
}

export function useChartViewport(range: TimeRange) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [state, setState] = useState<ViewState>({ from: 0, span: DEFAULT_SPAN_MS, pinned: true, by: "code" });

  const plotWidth = plotWidthOf(width);
  // Derived, never stored clamped: a history that grows (a young site) or a
  // "now" that moves re-clamps on the next render without an effect.
  const span = clampSpan(state.span, range);
  const from = state.pinned ? clampFrom(range.end - span, span, range) : clampFrom(state.from, span, range);
  const geo = useMemo(() => scrollGeometry(range, span, width, plotWidth), [range, span, width, plotWidth]);
  const pinned = state.pinned || from + span >= range.end - PIN_PX * geo.msPerPx;
  const canLeft = geo.maxScroll > 0 && from > range.start + geo.msPerPx;

  const live = useRef<Live>({ range, geo, plotWidth, view: { from, span, pinned } });
  useLayoutEffect(() => {
    live.current = { range, geo, plotWidth, view: { from, span, pinned } };
  });

  // The frame's width, before the first paint and on every resize.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Put scrollLeft where `from` is after a move in code, and after anything
  // that changes how time maps to pixels (zoom, a resize, the history's ends).
  const expected = useRef<number | null>(null);
  const applied = useRef<{ geo: ScrollGeometry; range: TimeRange } | null>(null);
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || width === 0) return;
    const moved = applied.current?.geo !== geo || applied.current?.range !== range;
    applied.current = { geo, range };
    if (!moved && state.by !== "code") return;
    const want = scrollOfFrom(from, range, geo);
    if (Math.abs(el.scrollLeft - want) < 1) return;
    expected.current = want;
    el.scrollLeft = want;
  }, [from, state.by, geo, range, width]);

  const frame = useRef(0);
  useLayoutEffect(() => () => cancelAnimationFrame(frame.current), []);

  const onScroll = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const el = scrollerRef.current;
      if (!el) return;
      const seen = expected.current;
      expected.current = null;
      if (seen !== null && Math.abs(el.scrollLeft - seen) < 1) return;
      const { range: r, geo: g, view } = live.current;
      setState({
        from: fromOfScroll(el.scrollLeft, r, g),
        span: view.span,
        pinned: el.scrollLeft >= g.maxScroll - PIN_PX,
        by: "scroll",
      });
    });
  }, []);

  /** Move to a view in code: clamped, pinned when it reaches now, scrollLeft follows. */
  const commit = useCallback((next: TimeView) => {
    const { range: r, plotWidth: w } = live.current;
    const nextSpan = clampSpan(next.span, r);
    const nextFrom = clampFrom(next.from, nextSpan, r);
    const nextPinned = nextFrom + nextSpan >= r.end - (PIN_PX * nextSpan) / Math.max(1, w);
    // Updated at once, so a second move before the next render starts from this one.
    live.current = { ...live.current, view: { from: nextFrom, span: nextSpan, pinned: nextPinned } };
    setState({ from: nextFrom, span: nextSpan, pinned: nextPinned, by: "code" });
  }, []);

  const getView = useCallback((): TimeView => {
    const { from: f, span: s } = live.current.view;
    return { from: f, span: s };
  }, []);

  /** Where a pointer is across the plot, 0 at its left edge and 1 at its right. */
  const anchorOf = useCallback((clientX: number): number => {
    const el = scrollerRef.current;
    const w = live.current.plotWidth;
    if (!el || w <= 0) return 0.5;
    return (clientX - el.getBoundingClientRect().left - PLOT_LEFT) / w;
  }, []);

  /** Zoom `base` to `span`, keeping the time under `anchor` where it is. */
  const zoomFrom = useCallback(
    (base: TimeView, nextSpan: number, anchor: number) => commit(zoomAround(base, nextSpan, anchor, live.current.range)),
    [commit],
  );

  /** A button or key zoom: around now while following it, else around the middle. */
  const zoomStep = useCallback(
    (dir: 1 | -1) => {
      const { view, range: r } = live.current;
      zoomFrom(view, nextZoomSpan(view.span, dir, r), view.pinned ? 1 : 0.5);
    },
    [zoomFrom],
  );
  const zoomIn = useCallback(() => zoomStep(-1), [zoomStep]);
  const zoomOut = useCallback(() => zoomStep(1), [zoomStep]);

  const toNow = useCallback(() => setState((s) => ({ ...s, pinned: true, by: "code" })), []);
  const toStart = useCallback(() => commit({ from: live.current.range.start, span: live.current.view.span }), [commit]);
  /** Put the window's start at `at` (the navigator). */
  const seek = useCallback((at: number) => commit({ from: at, span: live.current.view.span }), [commit]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      // Ctrl/Cmd + plus is the browser's own zoom; leave every modified key alone.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const view = live.current.view;
      switch (e.key) {
        case "ArrowLeft":
          commit({ from: view.from - PAN_SHARE * view.span, span: view.span });
          break;
        case "ArrowRight":
          commit({ from: view.from + PAN_SHARE * view.span, span: view.span });
          break;
        case "+":
        case "=":
          zoomIn();
          break;
        case "-":
        case "_":
          zoomOut();
          break;
        case "Home":
          toStart();
          break;
        case "End":
          toNow();
          break;
        default:
          return;
      }
      e.preventDefault();
    },
    [commit, zoomIn, zoomOut, toStart, toNow],
  );

  return {
    scrollerRef,
    width,
    plotWidth,
    geo,
    from,
    span,
    pinned,
    canLeft,
    canZoomIn: nextZoomSpan(span, -1, range) !== span,
    canZoomOut: nextZoomSpan(span, 1, range) !== span,
    onScroll,
    onKeyDown,
    zoomIn,
    zoomOut,
    toNow,
    seek,
    getView,
    anchorOf,
    zoomFrom,
  };
}

export type ChartViewport = ReturnType<typeof useChartViewport>;
